import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, Typography, TextField, Button, Box, CircularProgress, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/SendOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import api from '../api';

const statusIcon = (status) => {
  switch (status) {
    case 'delivered':
      return <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />;
    case 'failed':
      return <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />;
    case 'scheduled':
      return <ScheduleIcon color="warning" fontSize="small" sx={{ mr: 1 }} />;
    case 'sent':
    default:
      return <SendIcon color="primary" fontSize="small" sx={{ mr: 1 }} />;
  }
};

const ConversationModal = ({ open, onClose, propertyId, unitId, tenantEmail }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(`/api/properties/${propertyId}/units/${unitId}/messages?tenant=${tenantEmail}`)
        .then(res => setMessages(res.data))
        .catch(() => setMessages([]))
        .finally(() => setLoading(false));
    }
  }, [open, propertyId, unitId, tenantEmail]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      // Replace with your backend API for sending a message in a thread
      await api.post(`/api/properties/${propertyId}/units/${unitId}/messages`, {
        recipients: [tenantEmail],
        subject: '',
        body: newMessage,
      });
      setNewMessage('');
      // Refresh conversation
      const res = await api.get(`/api/properties/${propertyId}/units/${unitId}/messages?tenant=${tenantEmail}`);
      setMessages(res.data);
    } catch (err) {
      // Optionally show error
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Conversation with {tenantEmail}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close conversation">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 300 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <List>
            {messages.map((msg, idx) => (
              <ListItem key={idx} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: msg.sentBy === 'admin' ? 'flex-end' : 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {statusIcon(msg.status)}
                  <Typography variant="caption" color="text.secondary">
                    {msg.sentBy === 'admin' ? 'You' : msg.sentBy} • {new Date(msg.createdAt._seconds * 1000).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ bgcolor: msg.sentBy === 'admin' ? 'primary.light' : 'grey.200', p: 1, borderRadius: 2, maxWidth: 400 }}>
                  {msg.body}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <TextField
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          disabled={sending}
        />
        <Button onClick={handleSend} variant="contained" endIcon={<SendIcon />} disabled={sending || !newMessage.trim()}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationModal; 